import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, Pencil, Trash2, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/AdminLayout";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, apiError } from "@/lib/api";
import { TAB_LABELS, ROLE_LABELS } from "@/lib/i18n";

const EMPTY = { name: "", email: "", password: "", role: "cashier", group_ids: [], permissions: ["nuevo", "cobros"], can_manual_link: false };

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>
);

const PermsPicker = ({ value, onChange, role }) => (
  <div className="grid grid-cols-2 gap-2">
    {Object.entries(TAB_LABELS).filter(([k]) => k !== "ventas" || ["manager", "agent"].includes(role)).map(([k, l]) => (
      <label key={k} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
        <Checkbox checked={value.includes(k)} data-testid={`perm-${k}`}
          onCheckedChange={(c) => onChange(c ? [...value, k] : value.filter((x) => x !== k))} />{l}
      </label>
    ))}
  </div>
);

const GroupPicker = ({ groups, value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="user-group-picker">
    {groups.map((g) => (
      <label key={g.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
        <Checkbox checked={value.includes(g.id)} data-testid={`user-group-option-${g.id}`}
          onCheckedChange={(c) => onChange(c ? [...value, g.id] : value.filter((x) => x !== g.id))} />{g.name}
      </label>
    ))}
    {!groups.length && <p className="col-span-2 text-xs text-slate-400">Aún no hay grupos. Créalos con "Nuevo grupo".</p>}
  </div>
);

const UserDialog = ({ open, onOpenChange, groups, editing, onSaved }) => {
  const [v, setV] = useState(EMPTY);
  useEffect(() => { if (open) setV(editing ? { ...editing } : EMPTY); }, [open, editing]);
  const set = (k, x) => setV((s) => ({ ...s, [k]: x }));
  const submit = async () => {
    try {
      if (editing) await api.patch(`/users/${editing.id}`, { name: v.name, permissions: v.permissions, can_manual_link: v.can_manual_link, group_ids: v.group_ids || [] });
      else await api.post("/users", v);
      toast.success(editing ? "Usuario actualizado" : "Usuario creado"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e).message); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-shell max-w-lg" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Nombre"><Input value={v.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" /></Field>
          {!editing && <>
            <Field label="Correo"><Input type="email" value={v.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email-input" /></Field>
            <Field label="Contraseña (mínimo 8)"><Input type="password" value={v.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password-input" /></Field>
            <Field label="Rol">
              <Select value={v.role} onValueChange={(r) => set("role", r)}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="manager" data-testid="role-manager">Manager</SelectItem><SelectItem value="cashier" data-testid="role-cashier">Cajero</SelectItem><SelectItem value="agent" data-testid="role-agent">Agente de Reservas</SelectItem></SelectContent>
              </Select>
            </Field>
          </>}
          <Field label="Grupos (puedes elegir varios)"><GroupPicker groups={groups} value={v.group_ids || []} onChange={(x) => set("group_ids", x)} /></Field>
          <Field label="Pestañas permitidas"><PermsPicker role={v.role} value={v.permissions} onChange={(x) => set("permissions", x)} /></Field>
          <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
            <Checkbox checked={!!v.can_manual_link} onCheckedChange={(c) => set("can_manual_link", !!c)} data-testid="perm-manual-link" className="mt-0.5" />
            <span><span className="block font-medium text-slate-900">Puede usar enlace de pago manual</span><span className="block text-xs text-slate-500">Muestra la opción "Enlace de pago manual" en Nuevo cobro</span></span>
          </label>
          <p className="text-xs text-slate-500">Procesadores y Plantillas son solo de consulta. El Panel de ventas solo está disponible para managers y agentes de reservas (ventas de sus grupos).</p>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-[#2F5BFF] hover:bg-[#1F45E0]" data-testid="user-save-btn">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PasswordDialog = ({ target, onOpenChange }) => {
  const [pw, setPw] = useState("");
  useEffect(() => { setPw(""); }, [target]);
  const submit = async () => {
    try { await api.post(`/users/${target.id}/password`, { password: pw }); toast.success("Contraseña actualizada"); onOpenChange(false); }
    catch (e) { toast.error(apiError(e).message); }
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="admin-shell max-w-sm" data-testid="password-dialog">
        <DialogHeader><DialogTitle>Cambiar contraseña</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{target?.name} · {target?.email}</p>
        <Input type="password" placeholder="Nueva contraseña (mínimo 8)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="new-password-input" />
        <DialogFooter><Button onClick={submit} disabled={pw.length < 8} className="bg-[#2F5BFF] hover:bg-[#1F45E0]" data-testid="password-save-btn">Guardar contraseña</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserRow = ({ u, onEdit, onPassword, onDelete, onToggle }) => (
  <div className="flex flex-wrap items-center gap-3 px-5 py-4" data-testid={`user-row-${u.id}`}>
    <span className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-500"><UserRound size={16} /></span>
    <div className="min-w-[180px] flex-1">
      <p className="font-semibold text-slate-900">{u.name} <span className="ml-1 text-xs font-medium text-slate-400">{ROLE_LABELS[u.role]}</span></p>
      <p className="text-sm text-slate-500">{u.email}</p>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {u.permissions.length ? u.permissions.map((p) => <span key={p} className="rounded-full bg-[#EEF2FF] px-2 py-0.5 text-[11px] font-medium text-[#2F5BFF]">{TAB_LABELS[p]}</span>)
          : <span className="text-[11px] text-slate-400">Sin pestañas</span>}
        {u.can_manual_link && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-testid={`user-manual-badge-${u.id}`}>Enlace manual</span>}
      </div>
    </div>
    <label className="flex items-center gap-2 text-xs text-slate-500"><Switch checked={u.active} onCheckedChange={(a) => onToggle(u, a)} data-testid={`user-active-${u.id}`} />{u.active ? "Activo" : "Inactivo"}</label>
    <Button variant="ghost" size="sm" onClick={() => onEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil size={14} className="mr-1" />Editar</Button>
    <Button variant="ghost" size="sm" onClick={() => onPassword(u)} data-testid={`user-password-${u.id}`}><KeyRound size={14} className="mr-1" />Contraseña</Button>
    <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => onDelete(u)} data-testid={`user-delete-${u.id}`}><Trash2 size={14} /></Button>
  </div>
);

const GroupDialog = ({ target, onOpenChange, onSaved }) => {
  const [name, setName] = useState("");
  useEffect(() => { setName(target?.name || ""); }, [target]);
  const submit = async () => {
    try {
      if (target.id) await api.patch(`/groups/${target.id}`, { name }); else await api.post("/groups", { name });
      toast.success(target.id ? "Grupo renombrado" : "Grupo creado"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e).message); }
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="admin-shell max-w-sm" data-testid="group-dialog">
        <DialogHeader><DialogTitle>{target?.id ? "Renombrar grupo" : "Nuevo grupo"}</DialogTitle></DialogHeader>
        <Input placeholder="Ej. Cancún, Ventas Hard Rock" value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name-input" />
        <DialogFooter><Button onClick={submit} disabled={!name.trim()} className="bg-[#2F5BFF] hover:bg-[#1F45E0]" data-testid="group-save-btn">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ROLE_ORDER = { manager: 0, agent: 1, cashier: 2 };

const Group = ({ group, members, rowProps, onRename, onDelete }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`group-${group?.id || "none"}`}>
    <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-2.5">
      <p className="flex-1 text-xs font-bold uppercase tracking-wider text-slate-500">{group ? group.name : "Sin grupo"}</p>
      {group && <Button variant="ghost" size="sm" onClick={() => onRename(group)} data-testid={`group-rename-${group.id}`}><Pencil size={13} /></Button>}
      {group && <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => onDelete(group)} data-testid={`group-delete-${group.id}`}><Trash2 size={13} /></Button>}
    </div>
    <div className="divide-y divide-slate-100">
      {[...members].sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role]).map((u) => <UserRow key={u.id} u={u} {...rowProps} />)}
      {!members.length && <p className="px-5 py-4 text-sm text-slate-400">Sin usuarios en este grupo</p>}
    </div>
  </div>
);

export default function Users() {
  const [list, setList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupTarget, setGroupTarget] = useState(null);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [pwTarget, setPwTarget] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const load = () => Promise.all([api.get("/users"), api.get("/groups")]).then(([u, g]) => { setList(u.data); setGroups(g.data); });
  useEffect(() => { load(); }, []);

  const staff = list.filter((u) => u.role !== "admin");
  const toggle = async (u, active) => {
    try { await api.patch(`/users/${u.id}`, { active }); load(); } catch (e) { toast.error(apiError(e).message); }
  };
  const del = async () => {
    try { await api.delete(`/users/${toDelete.id}`); toast.success("Usuario eliminado"); load(); } catch (e) { toast.error(apiError(e).message); }
  };
  const delGroup = async (g) => {
    try { await api.delete(`/groups/${g.id}`); toast.success("Grupo eliminado"); load(); } catch (e) { toast.error(apiError(e).message); }
  };
  const rowProps = { onEdit: (u) => setDialog({ open: true, editing: u }), onPassword: setPwTarget, onDelete: setToDelete, onToggle: toggle };
  const orphans = staff.filter((u) => !groups.some((g) => u.group_ids.includes(g.id)));

  return (
    <>
      <PageHeader title="Usuarios y accesos" subtitle="Crea grupos, asigna cada manager, agente de reservas y cajero a uno o varios grupos y elige qué pestañas puede usar."
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setGroupTarget({})} data-testid="btn-add-group"><Plus size={16} className="mr-2" />Nuevo grupo</Button>
          <Button onClick={() => setDialog({ open: true, editing: null })} className="bg-[#2F5BFF] hover:bg-[#1F45E0]" data-testid="btn-add-user"><Plus size={16} className="mr-2" />Nuevo usuario</Button>
        </div>} />
      <div className="space-y-5">
        {groups.map((g) => <Group key={g.id} group={g} members={staff.filter((u) => u.group_ids.includes(g.id))} rowProps={rowProps} onRename={setGroupTarget} onDelete={delGroup} />)}
        {(orphans.length > 0 || !groups.length) && <Group group={null} members={orphans} rowProps={rowProps} />}
      </div>
      <UserDialog open={dialog.open} editing={dialog.editing} groups={groups} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={load} />
      <GroupDialog target={groupTarget} onOpenChange={(o) => { if (!o) setGroupTarget(null); }} onSaved={load} />
      <PasswordDialog target={pwTarget} onOpenChange={(o) => { if (!o) setPwTarget(null); }} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }} title="¿Eliminar usuario?" confirmLabel="Eliminar"
        description="Sus cobros se conservan." onConfirm={del} />
    </>
  );
}
